import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Sparkles,
  Users,
  Clock,
  ArrowRight,
  Radio,
  CalendarPlus,
  X as CloseIcon,
  Loader2,
  Check,
  Download,
  Share2,
  Copy,
  Link as LinkIcon,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { buildShareUrl, buildWhatsappHref } from "../lib/trainingShare";
import {
  buildIcs,
  downloadIcs,
  googleCalendarUrl,
  outlookCalendarUrl,
  slotWindowState,
  formatSlotIst,
  istToUtcDate,
} from "../lib/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_LABEL = { en: "English", hi: "Hindi", mr: "Marathi" };
const DEVICE_LABEL = { desktop: "Desktop", mobile: "Mobile" };

// Deterministic "attendee count" using the session id + slot as a seed.
// Uses a simple hash → within-hour curve: floor grows from ~min to ~min+8x.
function seededAttendees(session, slotIso, isLive) {
  const seed =
    (session.id + (slotIso || "")).split("").reduce((a, c) => a + c.charCodeAt(0), 0) || 1;
  const floor = Math.max(50, session.min_attendees_floor || 50);
  if (!isLive) {
    // Static "registered" count for the card when not live
    return floor + (seed % 180); // between floor and floor+180
  }
  // Live curve: minute-in-window 0 → 60 gives count 75 → 400 (approx)
  const start = istToUtcDate(slotIso).getTime();
  const mins = Math.max(0, Math.min(60, (Date.now() - start) / 60000));
  const base = 75 + (325 * mins) / 60;
  const jitter = (seed % 40) - 20; // ±20 deterministic
  return Math.round(base + jitter);
}

export default function Trainings() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("id");
  const isSingle = Boolean(slug);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {session, mode: "join_now" | "book_later", slot}
  const [tick, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        if (isSingle && focusId) {
          const { data } = await axios.get(`${API}/trainings/${focusId}`);
          setSessions([data]);
        } else {
          const { data } = await axios.get(`${API}/trainings`);
          setSessions(data);
        }
      } catch (e) {
        setSessions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [isSingle, focusId]);

  // Tick every 20s so LIVE / next-slot logic refreshes
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-[100dvh]">
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/40">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="trainings-home-btn"
              className="p-2 -ml-2 rounded-full text-[#64748B] hover:text-[#0F172A] hover:bg-white/60 transition-colors"
              aria-label="Home"
            >
              <Home className="w-5 h-5" />
            </button>
            <Logo className="h-7" />
            <span className="hidden sm:inline text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">
              Trainings
            </span>
          </div>
          <a
            href="https://channelpartner.network/signup"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold transition-all"
            data-testid="trainings-topbar-signup"
          >
            Sign Up <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-28 pb-16">
        <div className="mb-8">
          {isSingle ? (
            <>
              <button
                type="button"
                onClick={() => navigate("/trainings")}
                data-testid="single-see-all-top"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#2A5CB8] hover:text-[#FF5E1A] transition-colors"
              >
                <ArrowRight className="w-3 h-3 rotate-180" /> See all trainings
              </button>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A] mt-3 leading-tight max-w-3xl">
                {sessions[0]?.title || "Training"}
              </h1>
              <p className="text-[15px] md:text-base text-[#475569] leading-relaxed mt-2 max-w-2xl">
                Live ho toh turant join karo — warna calendar mein add karke reminder set karlo.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5" />
                FREE TRAININGS
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-[#0F172A] mt-3 leading-tight max-w-3xl">
                Aapke sales team ko industry ka best banao — free me.
              </h1>
              <p className="text-[15px] md:text-base text-[#475569] leading-relaxed mt-3 max-w-2xl">
                Short guided trainings on the real problems every CP faces. Session
                live ho toh turant join karo, warna calendar mein add karke apni
                phone ki reminder set karlo.
              </p>
            </>
          )}
        </div>

        {loading && <div className="text-slate-500">Loading sessions…</div>}

        {!loading && sessions.length === 0 && (
          <div className="glass-strong rounded-3xl p-8 text-center text-slate-500">
            {isSingle
              ? "This training is no longer available."
              : "No sessions scheduled yet. Check back soon."}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5" key={tick}>
          {sessions.map((s) => (
            <SessionCard key={s.id} session={s} onOpen={(mode, slot) => setModal({ session: s, mode, slot })} />
          ))}
        </div>

        {isSingle && !loading && sessions.length > 0 && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => navigate("/trainings")}
              data-testid="single-see-all-bottom"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] px-4 py-2.5 text-sm font-semibold text-[#0F172A]"
            >
              See all trainings <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {modal && (
          <BookingModal
            session={modal.session}
            initialMode={modal.mode}
            initialSlot={modal.slot}
            onClose={() => setModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ Share row (per-training link + WhatsApp) ============
const ShareRow = ({ session, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const url = buildShareUrl(session);
  const waHref = buildWhatsappHref(session, url);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* ignore */
    }
  };
  const pad = compact ? "px-2.5 py-1.5" : "px-3 py-2";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`share-row-${session.id}`}>
      <button
        type="button"
        onClick={copy}
        data-testid={`copy-link-${session.id}`}
        className={`inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-[#2A5CB8] text-[#0F172A] ${pad} text-xs font-semibold transition-colors`}
        aria-label="Copy shareable training link"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <LinkIcon className="w-3.5 h-3.5" />}
        {copied ? "Copied!" : "Copy link"}
      </button>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`share-training-${session.id}`}
        className={`inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white ${pad} text-xs font-semibold transition-colors`}
      >
        <Share2 className="w-3.5 h-3.5" /> WhatsApp
      </a>
    </div>
  );
};

// ============ Session card ============
const SessionCard = ({ session, onOpen }) => {
  const now = Date.now();
  // Sort slots ascending
  const sorted = useMemo(
    () => (session.slots || []).slice().sort((a, b) => istToUtcDate(a) - istToUtcDate(b)),
    [session.slots],
  );
  // Find current live slot, else next future slot
  const liveSlot = sorted.find((s) => {
    const w = slotWindowState(s, session.duration_minutes);
    return w.state === "live" || w.state === "opening";
  });
  const nextSlot = !liveSlot && sorted.find((s) => istToUtcDate(s).getTime() > now);
  const isLive = !!liveSlot;
  const attendees = seededAttendees(session, liveSlot || nextSlot || "", isLive);

  const langs = Array.from(new Set((session.variants || []).map((v) => v.lang)));
  const devices = Array.from(new Set((session.variants || []).map((v) => v.device)));

  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="glass-strong rounded-3xl p-5 md:p-6 flex flex-col md:flex-row gap-5 md:gap-6"
      data-testid={`training-card-${session.id}`}
    >
      <div className="w-full md:w-52 lg:w-56 md:shrink-0">
        {session.image_url ? (
          <div className="rounded-2xl overflow-hidden">
            <img
              src={session.image_url}
              alt={session.title}
              loading="lazy"
              className="w-full h-full object-cover block aspect-square md:aspect-[4/5]"
            />
          </div>
        ) : (
          <div className="rounded-2xl bg-[#EAF2FF] flex items-center justify-center aspect-square md:aspect-[4/5]">
            <Sparkles className="w-10 h-10 text-[#2A5CB8]" />
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col gap-4 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {session.category && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#2A5CB8] bg-[#EAF2FF] px-2 py-1 rounded-full">
            {session.category}
          </span>
        )}
        {isLive ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-white bg-red-500 px-2 py-1 rounded-full">
            <Radio className="w-3 h-3 animate-pulse" />
            LIVE
          </span>
        ) : (
          nextSlot && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF5E1A] bg-[#FFF3ED] px-2 py-1 rounded-full">
              <Clock className="w-3 h-3" />
              Next: {formatSlotIst(nextSlot)}
            </span>
          )
        )}
      </div>
      <div>
        <h3 className="font-heading text-xl md:text-2xl font-bold text-[#0F172A] leading-tight">
          {session.title}
        </h3>
        {session.frequency_label && (
          <p className="text-xs italic text-[#FF5E1A] font-semibold mt-1">
            {session.frequency_label}
          </p>
        )}
      </div>

      {session.description && (
        <p className="text-[13px] text-[#475569] leading-relaxed">{session.description}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {isLive ? (
          <span
            className="inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-200 px-3 py-1.5"
            data-testid={`watching-count-${session.id}`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            <span className="text-sm font-extrabold text-red-600">{attendees.toLocaleString()}</span>
            <span className="text-xs font-semibold text-red-500/90">watching now</span>
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF2FF] border border-[#CFE0FA] px-3 py-1.5"
            data-testid={`watching-count-${session.id}`}
          >
            <Users className="w-3.5 h-3.5 text-[#2A5CB8]" />
            <span className="text-sm font-extrabold text-[#2A5CB8]">{attendees.toLocaleString()}</span>
            <span className="text-xs font-semibold text-[#2A5CB8]/80">interested</span>
          </span>
        )}
        <span className="text-[11px] text-slate-400 inline-flex items-center gap-2">
          {langs.length > 0 && (
            <span className="inline-flex gap-1">
              {langs.map((l) => (
                <span key={l} className="uppercase font-bold tracking-wider text-slate-500">
                  {l}
                </span>
              ))}
            </span>
          )}
          {devices.length > 0 && (
            <span>· {devices.map((d) => DEVICE_LABEL[d]).join(" + ")}</span>
          )}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-1">
        {isLive ? (
          <button
            type="button"
            onClick={() => onOpen("join_now", liveSlot)}
            data-testid={`join-now-${session.id}`}
            className="flex-1 rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-3 text-base font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2 transition-all"
          >
            <Radio className="w-4 h-4" /> Join Now
          </button>
        ) : nextSlot ? (
          <>
            <button
              type="button"
              onClick={() => onOpen("book_later", nextSlot)}
              data-testid={`book-later-${session.id}`}
              className="flex-1 rounded-2xl bg-[#0F172A] hover:bg-[#1E293B] text-white px-4 py-3 text-base font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              <CalendarPlus className="w-4 h-4" /> Book for Later
            </button>
          </>
        ) : (
          <div className="text-sm text-slate-500 italic">No upcoming slots. Check back soon.</div>
        )}
      </div>
      <div className="pt-1">
        <ShareRow session={session} />
      </div>
      </div>
    </motion.div>
  );
};

// ============ Booking modal ============
const BookingModal = ({ session, initialMode, initialSlot, onClose }) => {
  const [step, setStep] = useState("lang"); // lang | device | slot | form | confirm
  const [lang, setLang] = useState(null);
  const [device, setDevice] = useState(null);
  const [slot, setSlot] = useState(initialSlot || null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);

  const langsAvailable = useMemo(
    () => Array.from(new Set((session.variants || []).map((v) => v.lang))),
    [session.variants],
  );
  const devicesAvailable = useMemo(
    () =>
      lang
        ? Array.from(
            new Set((session.variants || []).filter((v) => v.lang === lang).map((v) => v.device)),
          )
        : [],
    [session.variants, lang],
  );
  const supademoUrl = useMemo(() => {
    if (!lang || !device) return null;
    return (session.variants || []).find((v) => v.lang === lang && v.device === device)?.supademo_url;
  }, [session.variants, lang, device]);

  const futureSlots = useMemo(
    () =>
      (session.slots || [])
        .filter((s) => istToUtcDate(s).getTime() > Date.now() - 10 * 60 * 1000)
        .sort((a, b) => istToUtcDate(a) - istToUtcDate(b))
        .slice(0, 12),
    [session.slots],
  );

  // If lang/device gates auto-collapse to single option, skip forward
  useEffect(() => {
    if (step === "lang" && langsAvailable.length === 1) {
      setLang(langsAvailable[0]);
      setStep("device");
    }
  }, [step, langsAvailable]);
  useEffect(() => {
    if (step === "device" && devicesAvailable.length === 1) {
      setDevice(devicesAvailable[0]);
      setStep(initialMode === "join_now" ? "form" : "slot");
    }
  }, [step, devicesAvailable, initialMode]);

  const canJoinNow = () => {
    if (!slot) return false;
    const w = slotWindowState(slot, session.duration_minutes);
    return w.state === "live" || w.state === "opening";
  };

  const submit = async () => {
    if (!name.trim() || !/^[+\d\s-]{7,15}$/.test(phone.trim())) {
      setError("Please enter a valid name and phone number.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const mode = canJoinNow() ? "join_now" : "book_later";
      const { data } = await axios.post(`${API}/trainings/book`, {
        session_id: session.id,
        slot,
        name: name.trim(),
        phone: phone.trim(),
        language: lang,
        device,
        mode,
      });
      setBooking(data);
      // Persist locally so TrainingsJoin can resume from calendar link
      try {
        const local = JSON.parse(localStorage.getItem("cpn_bookings") || "{}");
        local[data.id] = { ...data };
        localStorage.setItem("cpn_bookings", JSON.stringify(local));
      } catch (_) {
        // ignore
      }
      if (mode === "join_now") {
        // fire and forget mark joined
        axios.put(`${API}/trainings/booking/${data.id}/joined`).catch(() => {});
        setStep("waiting");
      } else {
        setStep("confirm");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg glass-strong rounded-3xl p-6 md:p-7 max-h-[92dvh] overflow-y-auto"
        data-testid="training-booking-modal"
      >
        {step !== "waiting" && step !== "iframe" && (
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading text-xl font-bold text-[#0F172A]">{session.title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {lang && `Language: ${LANG_LABEL[lang]}`}
                {device && ` · Device: ${DEVICE_LABEL[device]}`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full text-slate-500 hover:bg-white/70"
              aria-label="Close"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {step === "lang" && langsAvailable.length > 1 && (
          <div>
            <p className="text-sm font-semibold text-[#0F172A] mb-3">Choose your language</p>
            <div className="grid grid-cols-3 gap-2">
              {langsAvailable.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => {
                    setLang(l);
                    setStep("device");
                  }}
                  data-testid={`lang-pick-${l}`}
                  className="p-4 rounded-2xl border-2 border-white/70 bg-white/60 hover:border-[#FF5E1A] hover:bg-[#FFF3ED] transition-all"
                >
                  <div className="font-bold text-[#0F172A]">{LANG_LABEL[l]}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "device" && devicesAvailable.length > 1 && (
          <div>
            <p className="text-sm font-semibold text-[#0F172A] mb-3">Choose your view</p>
            <div className="grid grid-cols-2 gap-2">
              {devicesAvailable.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    setDevice(d);
                    setStep(initialMode === "join_now" ? "form" : "slot");
                  }}
                  data-testid={`device-pick-${d}`}
                  className="p-4 rounded-2xl border-2 border-white/70 bg-white/60 hover:border-[#FF5E1A] hover:bg-[#FFF3ED] transition-all"
                >
                  <div className="font-bold text-[#0F172A]">{DEVICE_LABEL[d]}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "slot" && (
          <div>
            <p className="text-sm font-semibold text-[#0F172A] mb-3">Pick a slot</p>
            {futureSlots.length === 0 ? (
              <p className="text-sm text-slate-500">No upcoming slots.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-[50vh] overflow-y-auto">
                {futureSlots.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setSlot(s);
                      setStep("form");
                    }}
                    data-testid={`slot-pick-${s}`}
                    className={`text-left p-3 rounded-2xl border-2 transition-all ${
                      slot === s
                        ? "border-[#FF5E1A] bg-[#FFF3ED]"
                        : "border-white/70 bg-white/60 hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-[#0F172A]">{formatSlotIst(s)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "form" && (
          <div className="flex flex-col gap-4">
            <p className="text-sm font-semibold text-[#0F172A]">
              {canJoinNow() ? "Join the live session" : "Book your seat"}
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Full name</span>
              <input
                data-testid="training-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-3 py-3 rounded-2xl glass-input text-base text-[#0F172A]"
                placeholder="Aapka naam"
                required
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-600">Phone number</span>
              <input
                data-testid="training-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="px-3 py-3 rounded-2xl glass-input text-base text-[#0F172A]"
                placeholder="+91 98…"
                required
              />
            </label>
            {slot && (
              <div className="text-xs text-slate-500">
                Slot: <span className="font-semibold text-[#0F172A]">{formatSlotIst(slot)}</span>
              </div>
            )}
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="training-submit-btn"
              className="w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 text-base font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {canJoinNow() ? "Join session now" : "Confirm booking"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        )}

        {step === "confirm" && booking && (
          <ConfirmationPanel session={session} booking={booking} onClose={onClose} />
        )}

        {step === "waiting" && (
          <WaitingRoom
            session={session}
            supademoUrl={supademoUrl}
            onClose={onClose}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// ============ Confirmation with calendar buttons ============
const ConfirmationPanel = ({ session, booking, onClose }) => {
  const joinUrl = `${window.location.origin}/trainings/join/${booking.id}`;
  const payload = {
    title: session.title,
    description: session.description || "",
    joinUrl,
    startIst: booking.slot,
    durationMinutes: session.duration_minutes || 60,
  };
  const gcalHref = googleCalendarUrl(payload);
  const outlookHref = outlookCalendarUrl(payload);
  const handleIcs = () => {
    const ics = buildIcs(payload);
    downloadIcs(session.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase(), ics);
  };
  const trainingsUrl = `${window.location.origin}/trainings`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `Yeh free ChannelPartner.Network training miss mat karna — "${session.title}". Real estate sales ke real problems pe short guided sessions. Register karo yahan: ${trainingsUrl}`,
  )}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1.5 text-xs font-bold self-start">
        <Check className="w-3.5 h-3.5" />
        Booking confirmed
      </div>
      <h4 className="font-heading text-lg font-bold text-[#0F172A]">
        Aapki seat book ho gayi.
      </h4>
      <p className="text-sm text-[#475569] leading-relaxed">
        Slot: <span className="font-semibold text-[#0F172A]">{formatSlotIst(booking.slot)}</span>.
        Ab apne calendar mein add karlo — reminders 3 hours, 1 hour aur 10 minutes pehle
        aayenge (aapke phone ke calendar se).
      </p>
      <div className="flex flex-col gap-2">
        <a
          href={gcalHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cal-google-btn"
          className="w-full rounded-2xl bg-white border-2 border-slate-200 hover:border-[#FF5E1A] px-4 py-3 text-sm font-semibold text-[#0F172A] inline-flex items-center justify-center gap-2 transition-all"
        >
          <CalendarPlus className="w-4 h-4" /> Add to Google Calendar
        </a>
        <a
          href={outlookHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cal-outlook-btn"
          className="w-full rounded-2xl bg-white border-2 border-slate-200 hover:border-[#FF5E1A] px-4 py-3 text-sm font-semibold text-[#0F172A] inline-flex items-center justify-center gap-2 transition-all"
        >
          <CalendarPlus className="w-4 h-4" /> Add to Outlook
        </a>
        <button
          type="button"
          onClick={handleIcs}
          data-testid="cal-ics-btn"
          className="w-full rounded-2xl bg-white border-2 border-slate-200 hover:border-[#FF5E1A] px-4 py-3 text-sm font-semibold text-[#0F172A] inline-flex items-center justify-center gap-2 transition-all"
        >
          <Download className="w-4 h-4" /> Download .ics (Apple / Samsung / any)
        </button>
      </div>

      <div className="rounded-2xl bg-[#F0FAF3] border border-emerald-100 p-4 flex flex-col gap-2.5">
        <p className="text-sm font-semibold text-[#0F172A]">
          Share with your friends and colleagues from the industry
        </p>
        <p className="text-xs text-[#475569] leading-relaxed">
          Jitne zyada CPs, utna better session. Ek tap mein WhatsApp pe bhej do.
        </p>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp-btn"
          className="w-full rounded-2xl bg-[#25D366] hover:bg-[#1EBE5A] text-white px-4 py-3 text-sm font-bold inline-flex items-center justify-center gap-2 transition-all"
        >
          <Share2 className="w-4 h-4" /> Share on WhatsApp
        </a>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-xs text-slate-500 hover:text-[#FF5E1A] mt-2"
      >
        Done
      </button>
    </div>
  );
};

// ============ Waiting room + iframe ============
const WaitingRoom = ({ session, supademoUrl, onClose }) => {
  const [phase, setPhase] = useState("wait"); // wait | live
  const openedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setPhase("live"), 6800);
    return () => clearTimeout(t);
  }, []);

  const openLive = () => {
    if (!supademoUrl) return;
    window.open(supademoUrl, "_blank", "noopener,noreferrer");
  };

  // Best-effort: auto-open the live session in a new tab once we go live.
  useEffect(() => {
    if (phase === "live" && supademoUrl && !openedRef.current) {
      openedRef.current = true;
      window.open(supademoUrl, "_blank", "noopener,noreferrer");
    }
  }, [phase, supademoUrl]);

  if (phase === "live" && supademoUrl) {
    return (
      <div
        className="flex flex-col items-center gap-4 py-6 text-center"
        data-testid="training-live-open"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <Radio className="w-8 h-8 text-emerald-600 animate-pulse" />
        </div>
        <h4 className="font-heading text-lg font-bold text-[#0F172A]">
          You&apos;re in — session is live!
        </h4>
        <p className="text-sm text-[#475569] leading-relaxed max-w-xs">
          Session ek nayi tab mein khul rahi hai. Agar automatically nahi khuli,
          toh neeche button dabao.
        </p>
        <button
          type="button"
          onClick={openLive}
          data-testid="training-open-live-btn"
          className="w-full max-w-xs rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 text-base font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2"
        >
          <Radio className="w-4 h-4" /> Enter Live Session
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-slate-500 hover:text-[#FF5E1A]"
          data-testid="training-exit-btn"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-8"
      data-testid="training-waiting-room"
    >
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-[#FFF3ED] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#FF5E1A] animate-spin" />
        </div>
      </div>
      <p className="font-heading text-lg font-bold text-[#0F172A]">
        {session.waiting_caption || "Host is letting you in…"}
      </p>
      <FakeQueue />
      <p className="text-xs text-slate-500 text-center max-w-xs">
        Aapki seat confirm ho rahi hai. Please wait a few seconds.
      </p>
    </div>
  );
};

const FakeQueue = () => {
  const messages = [
    "Verifying your registration…",
    "3 people ahead of you…",
    "1 person ahead of you…",
    "Now connecting…",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const timers = messages.map((_, i) =>
      setTimeout(() => setIdx(i), 900 + i * 1400),
    );
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <p className="text-sm text-slate-600 transition-all">
      {messages[idx]}
    </p>
  );
};
